import { memo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import { useAnalytics } from 'use-analytics';
import TwAutocomplete from '../../../TwAutocomplete';
import TwRadio from '../../../TwRadio';
import Dropdown from '../../../Dropdown';
import Filter from '../../../Filters/Filter';
import {
  PortOfDepartureOption,
  AutocompleteType,
  PortOfDepartureLabel,
} from '../../../../constants';
import { autocompleteRequest } from '../../../../store/slices/autocomplete';
import { setCruiseSearchInputs } from '../../../../store/slices/tripSearch';

import useStyles from './styles';

const useStylesRadioGroup = makeStyles(() => ({
  root: {
    '& .MuiFormControlLabel-label': {
      fontFamily: 'var(--fontFamilyBody)',
    },
  },
}));

const PortsFilter = ({ variant }) => {
  const classes = useStyles();
  const classesRadioGroup = useStylesRadioGroup();
  const { track } = useAnalytics();
  const { origin, portOfDepartureOption, cityIds } = useSelector(
    (store) => store.tripSearch.searchInputs.cruise.filters,
  );
  const { portOfDepartureAutocompleteResults } = useSelector((store) => store.autocomplete);
  const dispatch = useDispatch();

  const isEmpty = () => portOfDepartureOption === PortOfDepartureOption.ANY;

  const getNonstopLabel = () =>
    PortOfDepartureLabel[PortOfDepartureOption.NONSTOP] + (origin || {}).label;

  const getLabel = () => {
    let labelToReturn = 'Ports of departure';

    if (!isEmpty()) {
      if (portOfDepartureOption === PortOfDepartureOption.SPECIFIC && cityIds.length) {
        labelToReturn = cityIds[0].label;
      } else if (portOfDepartureOption === PortOfDepartureOption.NONSTOP) {
        labelToReturn = getNonstopLabel();
      } else {
        labelToReturn = PortOfDepartureLabel[portOfDepartureOption];
      }
    }

    return labelToReturn;
  };

  const handleChange = (event, newValue) => {
    dispatch(
      setCruiseSearchInputs({
        filters: { portOfDepartureOption: Number.parseInt(newValue, 10) },
      }),
    );
    track('filterBar.handlePortsOfDepartureChange', {
      portOfDepartureOption: newValue,
    });
  };

  const handleSpecificPortsChange = (newValue) => {
    dispatch(setCruiseSearchInputs({ filters: { cityIds: newValue } }));
    track('filterBar.handleSpecificPortsChange', {
      cityIds: JSON.stringify(newValue),
    });
  };

  const handleSpecificPortsInputChange = (query) => {
    if (query && query.length < 2) return;

    dispatch(
      autocompleteRequest({
        autocompleteTypes: AutocompleteType.PORT_OF_DEPARTURE,
        responseType: 'portOfDepartureAutocompleteResults',
        query,
      }),
    );
  };

  const handleClear = () => {
    handleChange(null, PortOfDepartureOption.ANY);
  };

  const handleClose = () => {
    if (portOfDepartureOption === PortOfDepartureOption.SPECIFIC && !cityIds.length) {
      handleClear();
    }
  };

  useEffect(() => {
    if (portOfDepartureOption === PortOfDepartureOption.NONSTOP && !origin) {
      handleClear();
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [origin]);

  return (
    <Filter
      variant={variant}
      label={getLabel()}
      menuWidthClass="w-108"
      isFilterEmpty={isEmpty()}
      hasCounter
      itemCount={
        portOfDepartureOption === PortOfDepartureOption.SPECIFIC && cityIds.length
          ? cityIds.length - 1
          : 0
      }
      onClose={handleClose}
      handleClear={handleClear}
      filterMaxWidthClass="max-w-full"
    >
      <Dropdown.Item>
        <FormControl
          className="grid col-span-5 grid-cols-2 gap-y-2 w-full items-stretch"
          component="fieldset"
        >
          <RadioGroup
            name="portOfDepartureOption"
            value={portOfDepartureOption}
            onChange={handleChange}
            className="text-theme"
            classes={classesRadioGroup}
          >
            <FormControlLabel
              value={PortOfDepartureOption.ANY}
              control={<TwRadio />}
              label={PortOfDepartureLabel[PortOfDepartureOption.ANY]}
            />
            {origin && (
              <FormControlLabel
                value={PortOfDepartureOption.NONSTOP}
                control={<TwRadio />}
                label={getNonstopLabel()}
              />
            )}
            {/* <FormControlLabel
              value={PortOfDepartureOption.US}
              control={<TwRadio />}
              label={PortOfDepartureLabel[PortOfDepartureOption.US]}
            /> */}
            <FormControlLabel
              value={PortOfDepartureOption.SPECIFIC}
              control={<TwRadio />}
              label={PortOfDepartureLabel[PortOfDepartureOption.SPECIFIC]}
            />
          </RadioGroup>
          {portOfDepartureOption === PortOfDepartureOption.SPECIFIC && (
            <TwAutocomplete
              isMultiple
              placeholderText="Start typing..."
              onChange={handleSpecificPortsChange}
              onInputChange={handleSpecificPortsInputChange}
              classes={classes}
              options={portOfDepartureAutocompleteResults}
              values={cityIds}
              isNested
              disableCloseOnSelect
              disableClearable
            />
          )}
        </FormControl>
      </Dropdown.Item>
    </Filter>
  );
};

PortsFilter.propTypes = {
  variant: PropTypes.string,
};

PortsFilter.defaultProps = {
  variant: undefined,
};
export default memo(PortsFilter);
